import React, { useRef, useState } from 'react';

/**
 * Dropzone — the app had two incompatible one-off upload widgets before this
 * (EvidenceScheduleTab.tsx's real drag-and-drop zone styled entirely in raw Bootstrap utility
 * classes, and DocumentManager.tsx's plain file input with no drag-and-drop at all). This is one
 * shared, np- token-based dropzone both kinds of upload can use.
 */
interface DropzoneProps {
  onFilesSelected: (files: File[]) => void;
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  hint?: string;
  icon?: string;
}

export const Dropzone: React.FC<DropzoneProps> = ({
  onFilesSelected,
  accept,
  multiple = true,
  disabled = false,
  hint,
  icon = 'ri-upload-cloud-2-line',
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFilesSelected(Array.from(e.target.files ?? []));
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    onFilesSelected(Array.from(e.dataTransfer.files ?? []));
  };

  return (
    <div
      className={`np-dropzone ${isDragOver ? 'drag-over' : ''}`}
      style={{ cursor: disabled ? 'default' : 'pointer' }}
      onClick={() => !disabled && inputRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); if (!disabled) setIsDragOver(true); }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={disabled ? undefined : handleDrop}
    >
      <i className={`${icon} np-dropzone-icon`}></i>
      <span className="np-dropzone-label">
        {isDragOver ? 'Drop files to attach' : 'Drag & drop files here, or click to browse'}
      </span>
      {hint && <span className="np-sub small">{hint}</span>}
      <input
        ref={inputRef}
        type="file"
        multiple={multiple}
        className="d-none"
        accept={accept}
        onChange={handleFiles}
        disabled={disabled}
      />
    </div>
  );
};
